import { useEffect, useState } from "react";
import "./MobilKurulum.css";
import sahaLogo from "../assets/logo_saha.png";

const getResponseBody = async (response) => {
    const text = await response.text();
    if (!text) {
        return {};
    }

    try {
        return JSON.parse(text);
    } catch {
        return { MESSAGE: text };
    }
};

const getErrorMessage = (response, result, fallback) => {
    if (response.status === 429) {
        return "Çok fazla deneme yaptınız. Lütfen daha sonra tekrar deneyin.";
    }

    const validationMessage = result.errors
        ? Object.values(result.errors).flat().join(" ")
        : "";

    return result.MESSAGE || result.message || validationMessage || result.title || fallback;
};

export default function MobilKurulum() {
    const [email, setEmail] = useState("");
    const [platform, setPlatform] = useState("");
    const [otp, setOtp] = useState("");
    const [requestToken, setRequestToken] = useState(null);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    useEffect(() => {
        const previousLanguage = document.documentElement.lang;
        document.documentElement.lang = "tr";

        const meta = document.createElement("meta");
        meta.name = "google";
        meta.content = "notranslate";
        document.head.appendChild(meta);

        return () => {
            document.documentElement.lang = previousLanguage;
            meta.remove();
        };
    }, []);

    const requestOtp = async (event) => {
        event.preventDefault();
        setIsSending(true);
        setMessageType("info");
        setMessage("Doğrulama kodu gönderiliyor...");

        try {
            const response = await fetch("/api/record-mail/request-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    EMAIL: email.trim(),
                    PLATFORM: platform,
                    WEBSITE: ""
                })
            });
            const result = await getResponseBody(response);

            if (!response.ok) {
                throw new Error(getErrorMessage(response, result, "Doğrulama kodu gönderilemedi."));
            }
            if (!result.REQUEST_TOKEN) {
                throw new Error("Doğrulama isteği başlatılamadı. Lütfen tekrar deneyin.");
            }

            setRequestToken(result.REQUEST_TOKEN);
            setOtp("");
            setMessageType("success");
            setMessage(result.MESSAGE || "Doğrulama kodu e-posta adresinize gönderildi.");
            window.setTimeout(() => document.getElementById("otp")?.focus(), 0);
        } catch (error) {
            setRequestToken(null);
            setMessageType("error");
            setMessage(error.message || "Bir bağlantı hatası oluştu. Lütfen tekrar deneyin.");
        } finally {
            setIsSending(false);
        }
    };

    const verifyOtp = async (event) => {
        event.preventDefault();

        if (!requestToken) {
            setMessageType("error");
            setMessage("Önce doğrulama kodu istemelisiniz.");
            return;
        }
        if (!/^\d{6}$/.test(otp)) {
            setMessageType("error");
            setMessage("Doğrulama kodu 6 haneli olmalıdır.");
            return;
        }

        setIsVerifying(true);
        setMessageType("info");
        setMessage("Bilgiler doğrulanıyor...");

        try {
            const response = await fetch("/api/record-mail/verify-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ REQUEST_TOKEN: requestToken, OTP: otp })
            });
            const result = await getResponseBody(response);

            if (!response.ok) {
                throw new Error(getErrorMessage(response, result, "Doğrulama işlemi başarısız."));
            }

            setMessageType("success");
            setMessage(result.MESSAGE || "Kayıt bilgileriniz e-posta adresinize gönderildi.");
            setEmail("");
            setPlatform("");
            setOtp("");
            setRequestToken(null);
        } catch (error) {
            setMessageType("error");
            setMessage(error.message || "Bir bağlantı hatası oluştu. Lütfen tekrar deneyin.");
        } finally {
            setIsVerifying(false);
        }
    };

    return (
        <main className="mobil-kurulum-page notranslate" translate="no" lang="tr">
            <section className="mobil-kurulum-card" aria-labelledby="kurulum-title">
                <div className="mobil-kurulum-brand">
                    <img src={sahaLogo} alt="Sportive Saha" />
                </div>
                <h1 id="kurulum-title">Sportive Saha’ya Başlayın</h1>
                <p className="mobil-kurulum-intro">
                    E-posta adresinizi ve cihaz platformunuzu girin. Kurulum bilgileriniz,
                    doğrulamanın ardından kayıtlı e-posta adresinize gönderilecektir.
                </p>
                <p className="mobil-kurulum-note">
                    <strong>Not:</strong> Merkez kullanıcılar <strong>@sportive.com.tr</strong> e-posta adresini;
                    tüm mağaza çalışanlarımız ise iş başlangıcında İnsan Kaynakları Departmanı'na vermiş oldukları
                    e-posta adresini girmelidir.
                </p>

                <form onSubmit={requestOtp}>
                    <label htmlFor="email">E-posta</label>
                    <input
                        id="email"
                        type="email"
                        maxLength="200"
                        autoComplete="email"
                        placeholder="ad.soyad@sportive.com.tr"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        disabled={isSending || isVerifying}
                        required
                    />

                    <label htmlFor="platform">Platform</label>
                    <select
                        id="platform"
                        value={platform}
                        onChange={(event) => setPlatform(event.target.value)}
                        disabled={isSending || isVerifying}
                        required
                    >
                        <option value="">Seçiniz</option>
                        <option value="ANDROID">Android</option>
                        <option value="IOS">iOS</option>
                    </select>

                    <div className="mobil-kurulum-honeypot" aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input
                            id="website"
                            type="text"
                            tabIndex="-1"
                            autoComplete="off"
                            defaultValue=""
                        />
                    </div>

                    <button type="submit" disabled={isSending || isVerifying}>
                        {isSending ? "Kod gönderiliyor..." : requestToken ? "Yeni kod gönder" : "Doğrulama kodu gönder"}
                    </button>
                </form>

                {requestToken && (
                    <form className="otp-form" onSubmit={verifyOtp}>
                        <div className="step-divider"><span>2. Adım</span></div>
                        <label htmlFor="otp">Doğrulama kodu</label>
                        <input
                            id="otp"
                            className="otp-input"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength="6"
                            pattern="[0-9]{6}"
                            placeholder="••••••"
                            value={otp}
                            onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                            disabled={isVerifying}
                            required
                        />
                        <button type="submit" disabled={isVerifying || otp.length !== 6}>
                            {isVerifying ? "Doğrulanıyor..." : "Doğrula ve bilgileri gönder"}
                        </button>
                    </form>
                )}

                {message && (
                    <p className={`mobil-kurulum-message ${messageType}`} role="status" aria-live="polite">
                        {message}
                    </p>
                )}
            </section>
        </main>
    );
}
